import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminDashboardLayout from "../layout/AdminDashboardLayout";
import { getProfileById, deleteProfile, updateProfile } from "../../../services/adminService";
import {
 LinkedIn, GitHub, Language, LocationOn, BusinessCenter,
 Timeline, School, Verified, DeleteOutline, ArrowBackIosNew,
 AutoAwesome, Email, PhoneIphone,
 TrendingUp, TrendingDown, Lightbulb, Warning,
 ContentCopy, DoneAll, HistoryEdu, AssignmentInd,
 Edit, Check, Close, Translate, LocalLibrary, Apartment,
 Twitter, Facebook, Link, Male, Female, Transgender, Public, Interests
} from "@mui/icons-material";
import "./ProfileDetails.css";

const ProfileDetails = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const [profile, setProfile] = useState(null);
 const [loading, setLoading] = useState(true);
 const [activeTab, setActiveTab] = useState(0);
 const [copiedIndex, setCopiedIndex] = useState(null);

 const [isEditingLocation, setIsEditingLocation] = useState(false);
 const [tempLocation, setTempLocation] = useState("");

 useEffect(() => {
   fetchProfile();
 }, [id]);

 const fetchProfile = async () => {
   try {
     const res = await getProfileById(id);
     setProfile(res.data);
     setTempLocation(res.data.location || "");
   } catch (err) { 
     console.error(err); 
   } finally { 
     setLoading(false); 
   }
 };

 const handleFieldUpdate = async (fieldName, value) => {
  try {
     const updatedData = { ...profile, [fieldName]: value };
     const res = await updateProfile(id, updatedData);
     if (res.data) {
        setProfile(res.data);
        if (fieldName === 'location') setIsEditingLocation(false);
    }
  } catch (err) {
    alert(`Update Failed: ${err.response?.data?.message || "Error occurred"}`);
  }
 };

 const handleCopy = (text, index) => {
   navigator.clipboard.writeText(text);
   setCopiedIndex(index);
   setTimeout(() => setCopiedIndex(null), 2000);
 };

 const formatDate = (dateString) => {
   if (!dateString) return "Present";
   if (dateString.length === 4) return dateString;
   const date = new Date(dateString);
   return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
 };

 const formatUrl = (url) => {
    if (!url) return "#";
    return url.startsWith("http") ? url : `https://${url}`;
 };

 const getGenderIcon = (sex) => {
    const s = String(sex).toLowerCase();
    if (s === 'male') return <Male />;
    if (s === 'female') return <Female />;
    return <Transgender />;
 };

 const getSocialIcon = (network) => {
    const net = String(network).toLowerCase();
    if (net.includes('linkedin')) return <LinkedIn />;
    if (net.includes('twitter')) return <Twitter />;
    if (net.includes('facebook')) return <Facebook />;
    if (net.includes('github')) return <GitHub />;
    return <Link />;
 };

 if (loading) return (
   <AdminDashboardLayout>
     <div className="masterpiece-loader">
        <div className="spinner-glow"></div>
        <AutoAwesome className="loader-icon" />
     </div>
   </AdminDashboardLayout>
 );

 if (!profile) return (
   <AdminDashboardLayout>
     <div className="error-state-modern">
       <h2>Profile not found</h2>
       <button className="btn-modern-primary" onClick={() => navigate(-1)}>Return to Safety</button>
     </div>
   </AdminDashboardLayout>
 );

 const tabs = ["Intel", "Experience", "Education", "Expertise", "Communication", "AI Analysis"];

 return (
   <AdminDashboardLayout>
     <div className="profile-master-container">
       <header className="glass-header">
         <div className="header-left">
           <button className="back-circle-btn" onClick={() => navigate(-1)}>
             <ArrowBackIosNew fontSize="small" />
           </button>
           <div className="header-meta-stack">
             <div className="header-title-row">
                <h1>{profile.firstName} {profile.lastName}</h1>
                <span className={`badge-premium status-${String(profile.leadStatus).toLowerCase()}`}>
                    {profile.leadStatus === "true" || profile.leadStatus === true ? "Active" : "Inactive"}
                </span>
                <code className="id-pill-inline">#{id.slice(-8).toUpperCase()}</code>
             </div>
             <p className="header-subtitle">{profile.snippet || "Administrative Control Panel & Identity Insight"}</p>
           </div>
         </div>
         <button className="btn-delete-master" onClick={async () => {
           if(window.confirm("This action is permanent. Delete profile?")) {
             await deleteProfile(id); navigate("/profiles");
           }
         }}>
           <DeleteOutline fontSize="small" /> <span>Delete Profile</span>
         </button>
       </header>

       <main className="masterpiece-grid">
         <aside className="identity-monolith">
           <div className="glass-card profile-main-card">
             <div className="avatar-composition">
               <div className="avatar-ring">
                 <img src={profile.profilePicture || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400"} alt="Identity" />
               </div>
               <div className="verified-crown"><Verified fontSize="inherit" /></div>
             </div>
            
             <div className="identity-block">
                <h2>{profile.firstName} {profile.lastName}</h2>
                <p className="headline-text">{profile.currentPosition || profile.headline || "Elite Professional"}</p>
                {profile.socialProfiles && profile.socialProfiles.length > 0 && (
                    <div className="social-pill-container">
                        {profile.socialProfiles.map((social, idx) => (
                            <a 
                                key={idx} 
                                href={formatUrl(social.url)} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="social-mini-link" 
                                title={social.network}
                            >
                                {getSocialIcon(social.network)}
                            </a>
                        ))}
                    </div>
                )}
             </div>

             <div className="meta-list-modern">
               {profile.sex && (
                   <div className="meta-item-static">
                        <div className="meta-icon-box">{getGenderIcon(profile.sex)}</div>
                        <span style={{textTransform: 'capitalize'}}>{profile.sex}</span>
                   </div>
               )}

               <div className={`meta-item-editable ${isEditingLocation ? 'active-edit' : ''}`}>
                 <div className="meta-icon-box"><LocationOn /></div>
                 {isEditingLocation ? (
                   <div className="inline-morph-input">
                     <input 
                       value={tempLocation} 
                       onChange={(e) => setTempLocation(e.target.value)}
                       onKeyDown={(e) => e.key === 'Enter' && handleFieldUpdate('location', tempLocation)}
                       autoFocus
                     />
                     <div className="edit-controls">
                        <button className="save-dot" onClick={() => handleFieldUpdate('location', tempLocation)}><Check /></button>
                        <button className="close-dot" onClick={() => {setIsEditingLocation(false); setTempLocation(profile.location)}}><Close /></button>
                     </div>
                   </div>
                 ) : (
                   <div className="view-layer" onClick={() => setIsEditingLocation(true)}>
                     <span>{profile.location || "Location Not Set"}</span>
                     <Edit className="hover-edit-icon" />
                   </div>
                 )}
               </div>

               {profile.regions && profile.regions.length > 0 && (
                   <div className="meta-item-static">
                        <div className="meta-icon-box"><Public /></div>
                        <span>{profile.regions.join(", ")}</span>
                   </div>
               )}

               <div className="meta-item-static">
                 <div className="meta-icon-box"><BusinessCenter /></div>
                 <span>{profile.currentOrg || "No Organization"}</span>
               </div>
               <div className="meta-item-static">
                 <div className="meta-icon-box"><Timeline /></div>
                 <span>{profile.totalExperience || 0} Years Mastery</span>
               </div>
               <div className="meta-item-static">
                 <div className="meta-icon-box"><School /></div>
                 <span>{profile.highestEducation || "Academic Credentials"}</span>
               </div>
             </div>
           </div>
         </aside>

         <section className="workspace-monolith">
           <nav className="tab-navigation-premium">
             {tabs.map((tab, idx) => (
               <button 
                 key={tab} 
                 className={`premium-tab-btn ${activeTab === idx ? 'active' : ''}`} 
                 onClick={() => setActiveTab(idx)}
               >
                 {tab === "AI Analysis" && <AutoAwesome fontSize="inherit" />}
                 {tab}
               </button>
             ))}
           </nav>

           <div className="premium-viewport">
             {activeTab === 0 && (
               <div className="fade-in workspace-intel">
                  <div className="glass-panel intel-summary">
                     <div className="panel-luxury-header"><AssignmentInd /> <h3>Executive Summary</h3></div>
                     <p className="luxury-text">{profile.profileSummary || "Data stream unavailable."}</p>
                  </div>
                  <div className="glass-panel intel-journey">
                     <div className="panel-luxury-header"><HistoryEdu /> <h3>Professional Arc</h3></div>
                     <p className="luxury-text">{profile.professionalJourney || "History pending classification."}</p>
                  </div>
               </div>
             )}

             {activeTab === 1 && (
               <div className="premium-timeline fade-in">
                 {profile.experiences && profile.experiences.length > 0 ? (
                    profile.experiences.map((e, i) => (
                    <div className="timeline-block" key={i}>
                        <div className="block-rail">
                            <div className="rail-node"></div>
                        </div>
                        <div className="glass-card timeline-content-v2">
                            <div className="timeline-header-v2">
                                <div className="role-stack">
                                    <h3>{e.title || e.role || "Professional Role"}</h3>
                                    <div className="company-row">
                                        <Apartment fontSize="inherit" />
                                        <span>{e.company}</span>
                                    </div>
                                </div>
                                <span className="premium-date-pill">
                                    {formatDate(e.startDate)} — {formatDate(e.endDate)}
                                </span>
                            </div>
                            {e.summary && <p className="timeline-summary-text">{e.summary}</p>}
                        </div>
                    </div>
                    ))
                 ) : (
                    <div className="empty-state-card">No Experience Data Found</div>
                 )}
               </div>
             )}

             {activeTab === 2 && (
               <div className="education-premium-grid fade-in">
                 {profile.educations && profile.educations.length > 0 ? (
                    profile.educations.map((ed, i) => (
                    <div className="edu-modern-card" key={i}>
                        <div className="edu-card-top">
                            <div className="edu-icon-circle"><LocalLibrary /></div>
                            <span className="edu-year-tag">
                                {ed.startDate && ed.endDate ? `${ed.startDate} - ${ed.endDate}` : "Class of " + (ed.year || "N/A")}
                            </span>
                        </div>
                        <div className="edu-card-body">
                            <h3>{Array.isArray(ed.degree) ? ed.degree.join(" | ") : (ed.degree || "Degree")}</h3>
                            <p className="edu-specialization">
                                {Array.isArray(ed.majors) ? ed.majors.join(", ") : (ed.field || "General Studies")}
                            </p>
                            <div className="edu-institute-row">
                                <School fontSize="inherit" />
                                <span>{ed.school || ed.institute}</span>
                            </div>
                            {ed.location && <p className="edu-loc-text">{ed.location}</p>}
                        </div>
                    </div>
                    ))
                 ) : (
                    <div className="empty-state-card">No Education Records Found</div>
                 )}
               </div>
             )}

             {activeTab === 3 && (
               <div className="expertise-matrix fade-in">
                 <div className="matrix-row">
                   <div className="glass-panel expertise-box">
                     <div className="matrix-header">CORE STACK</div>
                     <div className="matrix-tags">
                       {profile.skills?.length > 0 ? profile.skills.map((s, i) => <span key={i} className="pill-primary">{s}</span>) : "N/A"}
                     </div>
                   </div>
                   <div className="glass-panel system-box">
                     <div className="matrix-header">INFRASTRUCTURE</div>
                     <div className="matrix-tags">
                       {profile.tags?.length > 0 ? profile.tags.map((t, i) => <span key={i} className="pill-dark">{t}</span>) : "N/A"}
                     </div>
                   </div>
                 </div>

                 <div className="glass-panel interests-box" style={{marginTop: '20px'}}>
                   <div className="matrix-header"><Interests fontSize="inherit" /> PERSONAL INTERESTS</div>
                   <div className="matrix-tags">
                     {profile.interests && profile.interests.length > 0 ? profile.interests.map((interest, i) => (
                        <span key={i} className="pill-interest">
                          {interest}
                        </span>
                     )) : "N/A"}
                   </div>
                 </div>
                 
                 <div className="glass-panel languages-box" style={{marginTop: '20px'}}>
                   <div className="matrix-header"><Translate fontSize="inherit" /> LINGUISTIC CAPABILITIES</div>
                   <div className="matrix-tags">
                     {profile.languages?.length > 0 ? profile.languages.map((l, i) => (
                        <div key={i} className="language-proficiency-card">
                          <div className="lang-info">
                            <Language fontSize="inherit" />
                            <span className="lang-name">{l.name || l}</span>
                          </div>
                          {l.proficiency && <span className="lang-level">{l.proficiency}</span>}
                        </div>
                     )) : "N/A"}
                   </div>
                 </div>
               </div>
             )}

             {activeTab === 4 && (
               <div className="comm-premium-layout fade-in">
                 <div className="comm-section-group">
                   <div className="comm-header">
                      <div className="comm-header-icon"><Email /></div>
                      <h3>Email Channels</h3>
                   </div>
                   <div className="comm-items-grid">
                     {profile.emails && profile.emails.length > 0 ? profile.emails.map((e, i) => (
                       <div key={i} className="comm-card-v2">
                          <div className="comm-card-info">
                            <label>{e.type ? e.type.replace(/_/g, ' ') : "Secure Route"}</label>
                            <span className="comm-card-val">{e.address || e}</span>
                          </div>
                          <button 
                            className={`copy-action-btn ${copiedIndex === `e-${i}` ? 'copied' : ''}`} 
                            onClick={() => handleCopy(e.address || e, `e-${i}`)}
                          >
                            {copiedIndex === `e-${i}` ? <DoneAll fontSize="small" /> : <ContentCopy fontSize="small" />}
                          </button>
                       </div>
                     )) : "No emails available"}
                   </div>
                 </div>

                 <div className="comm-section-group">
                   <div className="comm-header">
                      <div className="comm-header-icon"><PhoneIphone /></div>
                      <h3>Telephony</h3>
                   </div>
                   <div className="comm-items-grid">
                     {profile.phoneNumbers && profile.phoneNumbers.length > 0 ? profile.phoneNumbers.map((p, i) => (
                       <div key={i} className="comm-card-v2">
                          <div className="comm-card-info">
                            <label>{(p.type || "Mobile").toUpperCase()}</label>
                            <span className="comm-card-val">{p.number || p}</span>
                          </div>
                          <div className="comm-status-dot"></div>
                       </div>
                     )) : "No phone numbers available"}
                   </div>
                 </div>
               </div>
             )}

             {activeTab === 5 && (
               <div className="ai-diagnostic-center fade-in">
                 <div className="glass-panel ai-monolith-card">
                   <div className="ai-scan-line"></div>
                   <div className="ai-panel-header">
                      <AutoAwesome /> 
                      <span>AI EXECUTIVE SUMMARY</span>
                   </div>
                   <div className="ai-panel-body">
                     <p className="ai-report-prose">{profile.insights?.[0]?.profileSummary || "Insufficient data for synthesis."}</p>
                   </div>
                 </div>
                 <div className="swot-master-grid">
                   {[
                     { key: 'strengths', label: 'STRENGTHS', Icon: TrendingUp, color: 'emerald' },
                     { key: 'weaknesses', label: 'WEAKNESSES', Icon: TrendingDown, color: 'rose' },
                     { key: 'opportunities', label: 'OPPORTUNITIES', Icon: Lightbulb, color: 'amber' },
                     { key: 'threats', label: 'THREATS', Icon: Warning, color: 'fuchsia' }
                   ].map((item) => (
                     <div className={`swot-luxury-item ${item.color}`} key={item.key}>
                       <div className="swot-icon-box"><item.Icon /></div>
                       <div className="swot-info">
                          <label>{item.label}</label>
                          <p>{profile.insights?.[0]?.swotAnalysis?.[item.key] || "No data."}</p>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             )}
           </div>
         </section>
       </main>
     </div>
   </AdminDashboardLayout>
 );
};

export default ProfileDetails;